/**
 * Admin Feedback Page
 *
 * Paginated list of user feedback with type/status filters,
 * inline status & priority updates, and expandable detail view.
 * Access: platform_admin, platform_support
 */

import { useState } from "react";
import {
  Bug,
  ChatCircle,
  CaretDown as ChevronDown,
  CaretRight as ChevronRight,
  Clock,
  Lightbulb,
  ArrowsClockwise as RefreshCw,
  FloppyDisk as Save,
  Star,
  Trash,
  User,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAdminFeedback,
  useDeleteAdminFeedback,
  useUpdateAdminFeedback,
} from "@/hooks/useAdmin";
import { hasRole } from "@/components/auth/AdminGuard";
import type { AdminFeedbackItem } from "@/services/admin";
import { cn } from "@/utils/cn";

// ── Filter options ─────────────────────────────────────────

const TYPES = [
  { value: undefined, label: "All Types" },
  { value: "rating", label: "Rating" },
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature" },
  { value: "error_report", label: "Error" },
  { value: "general", label: "General" },
] as const;

const STATUSES = [
  { value: undefined, label: "All Status" },
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "wont_fix", label: "Won't Fix" },
] as const;

// ── Styling helpers ────────────────────────────────────────

const TYPE_ICONS: Record<string, React.ElementType> = {
  rating: Star,
  bug: Bug,
  feature_request: Lightbulb,
  error_report: WarningCircle,
  general: ChatCircle,
};

const TYPE_COLORS: Record<string, string> = {
  rating: "bg-amber-100 text-amber-700",
  bug: "bg-red-100 text-red-700",
  feature_request: "bg-violet-100 text-violet-700",
  error_report: "bg-orange-100 text-orange-700",
  general: "bg-blue-100 text-blue-700",
};

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-700",
  acknowledged: "bg-yellow-100 text-yellow-700",
  in_progress: "bg-indigo-100 text-indigo-700",
  resolved: "bg-green-100 text-green-700",
  wont_fix: "bg-zinc-100 text-zinc-700",
};

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-zinc-100 text-zinc-600",
  medium: "bg-blue-100 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  critical: "bg-red-100 text-red-600",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatLabel(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Detail panel ───────────────────────────────────────────

function FeedbackDetail({
  item,
  onClose,
}: {
  item: AdminFeedbackItem;
  onClose: () => void;
}) {
  const updateMutation = useUpdateAdminFeedback();
  const deleteMutation = useDeleteAdminFeedback();
  const isAdmin = hasRole("platform_admin");

  const [status, setStatus] = useState(item.status);
  const [priority, setPriority] = useState(item.priority);
  const [adminNotes, setAdminNotes] = useState(item.adminNotes ?? "");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const hasChanges =
    status !== item.status ||
    priority !== item.priority ||
    adminNotes !== (item.adminNotes ?? "");

  function handleSave() {
    const data: { status?: string; priority?: string; adminNotes?: string } =
      {};
    if (status !== item.status) data.status = status;
    if (priority !== item.priority) data.priority = priority;
    if (adminNotes !== (item.adminNotes ?? "")) data.adminNotes = adminNotes;

    updateMutation.mutate({ feedbackId: item.feedbackId, data });
  }

  function handleDelete() {
    deleteMutation.mutate(item.feedbackId, { onSuccess: onClose });
  }

  const TypeIcon = TYPE_ICONS[item.type] ?? ChatCircle;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col bg-card shadow-xl sm:max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Feedback Detail</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="space-y-5">
            {/* Meta badges */}
            <div className="flex flex-wrap gap-2">
              <span
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium",
                  TYPE_COLORS[item.type] ?? "bg-zinc-100 text-zinc-700"
                )}
              >
                {formatLabel(item.type)}
              </span>
              <span
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium",
                  STATUS_COLORS[item.status] ?? "bg-zinc-100 text-zinc-700"
                )}
              >
                {formatLabel(item.status)}
              </span>
              <span
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium",
                  PRIORITY_COLORS[item.priority] ?? "bg-zinc-100 text-zinc-700"
                )}
              >
                {formatLabel(item.priority)}
              </span>
              {item.rating && (
                <span className="flex items-center gap-0.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                  <Star className="h-3 w-3" weight="fill" />
                  {item.rating}/5
                </span>
              )}
            </div>

            {/* Title */}
            {item.title && (
              <h3 className="text-base font-semibold">{item.title}</h3>
            )}

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                Description
              </p>
              <p className="text-sm whitespace-pre-wrap">{item.description}</p>
            </div>

            {/* Error details */}
            {item.errorMessage && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                  Error Message
                </p>
                <pre className="overflow-x-auto rounded-lg bg-muted p-3 text-xs">
                  {item.errorMessage}
                </pre>
              </div>
            )}

            {item.stackTrace && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase mb-1">
                  Stack Trace
                </p>
                <pre className="max-h-40 overflow-auto rounded-lg bg-muted p-3 text-xs">
                  {item.stackTrace}
                </pre>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">User ID</p>
                <p className="font-mono text-xs">
                  {item.userId.slice(0, 12)}...
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-xs">{formatDate(item.createdAt)}</p>
              </div>
              {item.pageUrl && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Page URL</p>
                  <p className="truncate text-xs">{item.pageUrl}</p>
                </div>
              )}
              {item.userAgent && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">User Agent</p>
                  <p className="truncate text-xs">{item.userAgent}</p>
                </div>
              )}
            </div>

            {/* Admin controls */}
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className="text-sm font-semibold">Admin Actions</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="fb-status"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Status
                  </label>
                  <select
                    id="fb-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
                  >
                    {STATUSES.filter((s) => s.value).map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="fb-priority"
                    className="mb-1 block text-xs font-medium text-muted-foreground"
                  >
                    Priority
                  </label>
                  <select
                    id="fb-priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
                  >
                    {["low", "medium", "high", "critical"].map((p) => (
                      <option key={p} value={p}>
                        {formatLabel(p)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="fb-notes"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Admin Notes
                </label>
                <textarea
                  id="fb-notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  maxLength={5000}
                  className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm resize-none"
                  placeholder="Internal notes about this feedback..."
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || updateMutation.isPending}
                >
                  {updateMutation.isPending ? (
                    <Spinner size="sm" className="mr-1.5" />
                  ) : (
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Save Changes
                </Button>

                {isAdmin && !showConfirmDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConfirmDelete(true)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash className="mr-1.5 h-3.5 w-3.5" />
                    Delete
                  </Button>
                )}

                {isAdmin && showConfirmDelete && (
                  <>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? (
                        <Spinner size="sm" className="mr-1.5" />
                      ) : (
                        "Confirm Delete"
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>

              {updateMutation.isError && (
                <p className="text-xs text-destructive">
                  Failed to update. Please try again.
                </p>
              )}
              {updateMutation.isSuccess && (
                <p className="text-xs text-green-600">Updated successfully.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────

export function AdminFeedbackPage() {
  const [selectedType, setSelectedType] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [cursor, setCursor] = useState<string | undefined>();
  const [previousItems, setPreviousItems] = useState<AdminFeedbackItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useAdminFeedback({
    type: selectedType,
    status: selectedStatus,
    cursor,
    limit: 25,
  });

  const displayItems = [...previousItems, ...(data?.items ?? [])];

  function handleLoadMore() {
    if (data?.items) {
      setPreviousItems((prev) => [...prev, ...data.items]);
    }
    setCursor(data?.cursor);
  }

  function handleFilterChange(
    setter: (v: string | undefined) => void,
    value: string | undefined
  ) {
    setter(value);
    setCursor(undefined);
    setPreviousItems([]);
  }

  const expandedItem = expandedId
    ? displayItems.find((i) => i.feedbackId === expandedId)
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Feedback
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review and manage user feedback, bug reports, and feature requests.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {TYPES.map(({ value, label }) => (
          <button
            key={label}
            onClick={() => handleFilterChange(setSelectedType, value)}
            className={cn(
              "rounded-lg px-3.5 py-2 text-sm font-medium transition-colors",
              selectedType === value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {label}
          </button>
        ))}
        <div className="w-px bg-border mx-1" />
        {STATUSES.map(({ value, label }) => (
          <button
            key={label}
            onClick={() => handleFilterChange(setSelectedStatus, value)}
            className={cn(
              "rounded-lg px-3.5 py-2 text-sm font-medium transition-colors",
              selectedStatus === value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Feedback list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          Failed to load feedback.
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {displayItems.map((item) => {
              const TypeIcon = TYPE_ICONS[item.type] ?? ChatCircle;
              return (
                <div
                  key={item.feedbackId}
                  role="button"
                  tabIndex={0}
                  className="flex cursor-pointer flex-col gap-2 rounded-xl border border-border/50 bg-card p-4 transition-colors hover:bg-muted/20 sm:flex-row sm:items-center sm:justify-between"
                  onClick={() => setExpandedId(item.feedbackId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setExpandedId(item.feedbackId);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <TypeIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {item.title || item.description.slice(0, 80)}
                        {!item.title && item.description.length > 80
                          ? "..."
                          : ""}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {item.userId.slice(0, 8)}...
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(item.createdAt)}
                        </span>
                        {item.rating && (
                          <span className="inline-flex items-center gap-0.5">
                            <Star
                              className="h-3 w-3 text-amber-500"
                              weight="fill"
                            />
                            {item.rating}/5
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:shrink-0">
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        TYPE_COLORS[item.type] ?? "bg-zinc-100 text-zinc-700"
                      )}
                    >
                      {formatLabel(item.type)}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        STATUS_COLORS[item.status] ??
                          "bg-zinc-100 text-zinc-700"
                      )}
                    >
                      {formatLabel(item.status)}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        PRIORITY_COLORS[item.priority] ??
                          "bg-zinc-100 text-zinc-700"
                      )}
                    >
                      {item.priority}
                    </span>
                    {expandedId === item.feedbackId ? (
                      <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                    ) : (
                      <ChevronRight className="hidden h-4 w-4 text-muted-foreground sm:block" />
                    )}
                  </div>
                </div>
              );
            })}

            {displayItems.length === 0 && !isLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No feedback items found.
              </div>
            )}
          </div>

          {data?.cursor && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail drawer */}
      {expandedItem && (
        <FeedbackDetail
          item={expandedItem}
          onClose={() => setExpandedId(null)}
        />
      )}
    </div>
  );
}
